import Carousel from '@components/Carousel'
import SearchBar from '@components/SearchBar/SearchBar'
import { SidebarMobileToggle } from '@components/Sidebar/MobileToggle'
import TokenAvatar from '@components/TokenAvatar'
import { TokenList } from '@components/TokenList/TokenList'
import { ChainTabItem } from '@modules/Tokens/Components/TokenManager'
import { useDiscoverData } from '@plug-core/hooks/useDiscoverData'
import { Link } from '@plug-core/idls/plugTokenRegistry/plugTokenRegistry.did'
import { formatMoney } from '@plug-core/utils/formatNumber'
import { Avatar, Heading, ListGroup, ListRow, Text } from '@ui'
import { ROUTES } from '@utils/routes'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

const Title = ({ primary, secondary }: { primary: string; secondary?: string }) => (
  <div className="mx-1 mb-2 flex items-center justify-between">
    <Heading level={4}>{primary}</Heading>
    {secondary && (
      <Text variant="body-s" tone="fg-2">
        {secondary}
      </Text>
    )}
  </div>
)

const LinkRow = ({ link }: { link: Link }) => (
  <ListRow
    onClick={() => window.open(link.link, '_blank')}
    leading={<Avatar src={link.image} size={40} fallback={link.title} />}
    primary={link.title}
    secondary={link.description[0]}
  />
)

export const Discover = () => {
  const {
    topTradedTokens,
    links,
    selectedChain,
    setSelectedChain,
    search,
    searchTerm,
    searchResults,
  } = useDiscoverData()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const dapps = links.filter((l) => l.kind === 'dapp' && l.chain[0] === selectedChain)

  // While searching, results replace the page content (no overlay, so the background art shows through).
  const searching = searchTerm.length > 1

  return (
    <div className="mb-3 flex flex-col">
      <SidebarMobileToggle title="Discover" />

      <div className="mb-4">
        <SearchBar
          searchTerm={searchTerm}
          setSearchTerm={search}
          placeholder={t('discover.searchPlaceholder')}
        />
      </div>

      {!searching && (
        <>
          <Title primary={t('discover.articles')} />

          <div className="mb-9 flex min-h-[164px] flex-col">
            <Carousel itemSize={270} spacingPx={16} hideControls showPager>
              {links
                .filter((l) => l.kind === 'article')
                .map((link, i, arr) => (
                  <a
                    key={link.id}
                    href={link.link}
                    target="_blank"
                    className="block shrink-0 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                    style={{ marginRight: i === arr.length - 1 ? 16 : 0 }}
                  >
                    <img
                      src={link.image}
                      alt={link.title}
                      height={160}
                      width={270}
                      className="block h-[160px] w-[270px] rounded-lg border border-solid border-hairline object-cover"
                    />
                  </a>
                ))}
            </Carousel>
          </div>

          <div className="no-scrollbar mb-2 flex flex-row overflow-x-auto whitespace-nowrap">
            {topTradedTokens.map(({ chain }) => (
              <ChainTabItem
                key={chain}
                selected={chain === selectedChain}
                onPress={() => setSelectedChain(chain)}
                chain={chain}
              />
            ))}
          </div>

          <Title primary={t('discover.leaderboard')} secondary={t('discover.volume')} />

          {topTradedTokens.map((list) => {
            if (list.chain !== selectedChain) return

            return (
              <ListGroup key={list.chain}>
                {list.tokens.slice(0, 5).map(({ token, volume }) => (
                  <ListRow
                    key={token.symbol}
                    dense
                    onClick={() => navigate(ROUTES.TOKEN_DETAILS(token.id))}
                    leading={
                      <TokenAvatar size={34} logo={token.logo} fallbackName={token.symbol} />
                    }
                    primary={token.symbol}
                    trailing={
                      <Text as="span" variant="body-s" tone="fg-2" tabular>
                        {formatMoney(volume)}
                      </Text>
                    }
                  />
                ))}
              </ListGroup>
            )
          })}

          <div className="h-5" />
          <Title primary={t('discover.dapps')} />

          {dapps.length > 0 && (
            <ListGroup>
              {dapps.map((link) => (
                <LinkRow key={link.id} link={link} />
              ))}
            </ListGroup>
          )}
        </>
      )}

      {searching && (
        <div className="flex flex-col gap-5 pb-4">
          {searchResults.tokens[0] && (
            <section className="flex flex-col">
              <Title primary={t('discover.tokens')} />

              <TokenList
                balances={searchResults.tokens}
                onTokenClick={({ token }) => navigate(ROUTES.TOKEN_DETAILS(token.id))}
                showPriceChange
              />
            </section>
          )}

          {searchResults.links[0] && (
            <section className="flex flex-col">
              <Title primary={t('discover.articles')} />

              <ListGroup>
                {searchResults.links.map((link) => (
                  <LinkRow key={link.id} link={link} />
                ))}
              </ListGroup>
            </section>
          )}

          {searchResults.dapps[0] && (
            <section className="flex flex-col">
              <Title primary={t('discover.dapps')} />

              <ListGroup>
                {searchResults.dapps.map((dapp) => (
                  <LinkRow key={dapp.id} link={dapp} />
                ))}
              </ListGroup>
            </section>
          )}

          {Object.values(searchResults).flat().length === 0 && (
            <div className="flex min-h-[200px] items-center justify-center">
              <Text tone="fg-2">{t('discover.noResults')}</Text>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
